// RoleSwitcher — topbar popover for switching between roles (task 42.3).
//
// Per DESIGN.md §9 and R5.3:
//   - Visible ONLY when `distinct_roles.length > 1`.
//   - Click → popover listing every distinct role.
//   - Select → updates `sessionStorage.currentRole` via RoleProvider.setRole,
//     closes popover, pages re-fetch via the scopedFetchToken dep. NO full
//     page reload (DESIGN.md §9: "re-fetches the current page's data. No
//     full page reload").
//   - Keyboard: Escape closes, Arrow keys navigate, Enter selects.
//
// Final visual treatment (popover elevation, chip colors, caption with
// location count) lands in task 43.

import { useCallback, useEffect, useRef, useState } from "react";
import { useRole } from "@/role";

export function RoleSwitcher(): JSX.Element | null {
  const { currentRole, distinctRoles, setRole, status } = useRole();
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // DESIGN.md §9: "Hidden entirely if distinct_roles.length <= 1."
  // Also hide while the picker is still pending — no active role to show.
  const shouldRender = distinctRoles.length > 1 && status !== "pending";

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSelect = useCallback(
    (role: string): void => {
      if (role !== currentRole) setRole(role);
      setOpen(false);
      buttonRef.current?.focus();
    },
    [currentRole, setRole],
  );

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-role-option]"),
    );
    const idx = items.findIndex((b) => b === document.activeElement);
    if (idx < 0) {
      items[0]?.focus();
      return;
    }
    const delta = e.key === "ArrowDown" ? 1 : -1;
    const next = items[(idx + delta + items.length) % items.length];
    next?.focus();
  };

  if (!shouldRender) return null;

  return (
    <div className="role-switcher" ref={rootRef}>
      <button
        type="button"
        className="role-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          currentRole !== null
            ? `Current role: ${currentRole}. Change role.`
            : "Choose role"
        }
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="role-switcher__current">{currentRole ?? "—"}</span>
        <span className="role-switcher__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul
          className="role-switcher__popover"
          role="listbox"
          aria-label="Select a role"
          onKeyDown={handleListKeyDown}
        >
          {distinctRoles.map((role) => {
            const selected = role === currentRole;
            return (
              <li key={role} role="presentation">
                <button
                  type="button"
                  className="role-switcher__option"
                  role="option"
                  aria-selected={selected}
                  data-role-option
                  onClick={() => handleSelect(role)}
                >
                  <span className="role-switcher__option-name">{role}</span>
                  {selected && (
                    <span className="role-switcher__check" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
